import { enumKeyFromValue, falsey, plusBusinessDays } from '@/helpers';
import { i18n } from '@/i18n';
import { useProjectsStore } from '@/store/projects';
import { DateTime } from 'luxon';
import { Model, useRepo } from 'pinia-orm';
import { Attr, BelongsTo, HasMany, HasOne, MorphMany, MorphOne, Num, Str } from 'pinia-orm/dist/decorators';
import Claim from './Claim';
import Comment from './Comment';
import ContractEvent from './ContractEvent';
import Correspondence from './Correspondence';
import Delay from './Delay';
import Package from './Package';
import PackageTradeItem from './PackageTradeItem';
import Project from './Project';
import TradeItem from './TradeItem';
import User from './User';
import VariationOrderCategory from './VariationOrderCategory';
import VariationOrderCost from './VariationOrderCost';
/* --- user header --- */
/* --- end user header --- */

export const VariationOrderType = {
  variation: 0,
  provisional_sum: 1,
  time_extension: 2,
  other: 3,
} as const;

export const VariationOrderStatus = {
  raised: 0,
  registered: 10,
  priced: 20,
  submitted: 30,
  provisioned: 40,
  principled: 41,
  approved: 50,
  completed: 60,
  declined: 70,
} as const;

export const VariationOrderTitles = {
  variation: 'Variations',
  provisional_sum: 'Provisional Sums',
  time_extension: 'Extension of Time',
  other: 'Other',
} as const;

export const VariationOrderStatusTitles = {
  raised: 'Raised',
  registered: 'Registered',
  priced: 'Forecasted',
  submitted: 'Submitted',
  provisioned: 'Assessed',
  principled: 'Approved in Principle',
  approved: 'Approved',
  completed: 'Completed',
  declined: 'Declined',
} as const;

export const VariationOrderStatusColours = {
  raised: '#B80D15',
  registered: '#A6ACEC',
  priced: '#4E95D9',
  submitted: '#215F9A',
  provisioned: '#163E64',
  principled: '#123353',
  approved: '#0E2841',
  completed: '#196B24',
  declined: '#000',
} as const;

export const VariationOrderStatusTextColours = {
  raised: '#FFF',
  registered: '#000',
  priced: '#000',
  submitted: '#000',
  provisioned: '#FFF',
  principled: '#FFF',
  approved: '#FFF',
  completed: '#FFF',
  declined: '#FFF',
} as const;

export const VariationOrderCorrespondenceTypes = {
  'notice-of-variation': 1,
  'notice-of-pricing': 2,
  'approval-of-variation': 3,
} as const;

export default class VariationOrder extends Model {
  static entity = 'App\\Models\\VariationOrder';
  // fields
  @Num(0)
  declare id: number;
  @Str('')
  declare code: string;
  @Str('')
  declare description: string;
  @Num(0)
  declare project_id: number;
  @Num(0)
  declare package_id: number;
  @Num(undefined)
  declare head_contract_variation_id: number | undefined;
  @Num(undefined)
  declare variation_order_category_id: number | undefined;
  @Num(undefined)
  declare head_contract_trade_item_id: number | undefined;
  @Num(undefined)
  declare package_trade_item_id: number | undefined;
  @Num(0)
  declare user_id: number;
  @Num(0)
  declare delay_id: number;
  @Num(0)
  declare next_action_assignee_id: number;
  @Str('')
  declare deleted_at: string | undefined;
  @Str('')
  declare created_at: string | undefined;
  @Str('')
  declare updated_at: string | undefined;
  @Str('')
  declare registered_at: string | undefined;
  @Str('')
  declare submitted_at: string | undefined;
  @Str('')
  declare provisioned_at: string | undefined;
  @Str('')
  declare approved_at: string | undefined;
  @Str('')
  declare notice_sent_at: string | undefined;
  @Str('')
  declare date_of_occurrence: string | undefined;
  @Str('')
  declare next_action_due_date: string | undefined;
  @Str(undefined)
  declare notes: string | undefined;
  @Str(undefined)
  declare next_action: string | undefined;
  @Str(undefined)
  declare client_ref: string | undefined;
  @Num(undefined)
  declare budget: number | undefined;
  @Num(undefined)
  declare baseline_forecast_amount: number | undefined;
  @Attr([])
  declare actual_forecast_amount: number[];
  @Num(undefined)
  declare type: ObjectValues<typeof VariationOrderType>;
  @Num(undefined)
  declare status: ObjectValues<typeof VariationOrderStatus>;
  @Str(undefined)
  declare transferred_at: string | undefined;
  @Num(undefined)
  declare transferred_to_variation_order_id: number | undefined;
  @Num(undefined)
  declare transferred_from_variation_order_id: number | undefined;
  // relations
  @BelongsTo(() => Project, 'project_id', 'id')
  declare project: Project;
  @BelongsTo(() => Package, 'package_id', 'id')
  declare package: Package;
  @BelongsTo(() => Delay, 'delay_id', 'id')
  declare delay: Delay;
  @HasMany(() => VariationOrderCost, 'variation_order_id', 'id')
  declare costs: VariationOrderCost[];
  @HasMany(() => TradeItem, 'variation_order_id', 'id')
  declare trade_items: TradeItem[];
  @BelongsTo(() => User, 'user_id', 'id')
  declare user: User;
  @BelongsTo(() => User, 'next_action_assignee_id', 'id')
  declare next_action_assignee: User;
  @MorphMany(() => Comment, 'commentable_id', 'commentable_type', 'id')
  declare comments: Comment[];
  @MorphMany(() => Correspondence, 'correspondent_id', 'correspondent_type', 'id')
  declare correspondences: Correspondence[];
  @BelongsTo(() => VariationOrder, 'head_contract_variation_id', 'id')
  declare head_contract_variation: VariationOrder;
  @HasMany(() => VariationOrder, 'head_contract_variation_id', 'id')
  declare subcontract_variation_orders: VariationOrder[];
  @BelongsTo(() => VariationOrderCategory, 'variation_order_category_id', 'id')
  declare variation_order_category: VariationOrderCategory | undefined;
  @BelongsTo(() => TradeItem, 'head_contract_trade_item_id', 'id')
  declare head_contract_trade_item: TradeItem | undefined;
  @BelongsTo(() => PackageTradeItem, 'package_trade_item_id', 'id')
  declare package_trade_item: PackageTradeItem | undefined;
  @BelongsTo(() => VariationOrder, 'transferred_to_variation_order_id', 'id')
  declare transferred_to_variation_order: VariationOrder | undefined;
  @BelongsTo(() => VariationOrder, 'transferred_from_variation_order_id', 'id')
  declare transferred_from_variation_order: VariationOrder | undefined;
  @Attr(undefined)
  declare $previous: number | undefined;
  @Attr(undefined)
  declare $linkedTradeItemBudget: number | undefined;
  @Attr(undefined)
  declare $otherLinkedVariationsBudget: number | undefined;
  @Attr(undefined)
  declare $hasCosts: boolean | undefined;
  @Attr(undefined)
  declare $totalCertified: number | undefined;
  @Attr(undefined)
  declare $totalClaim: number | undefined;
  @Attr(undefined)
  declare $hasMedia: boolean | undefined;
  @HasOne(() => ContractEvent, 'variation_order_id', 'id')
  declare contract_event: ContractEvent;
  /* --- user code --- */

  get totalBudget() {
    return this.budget ?? this.trade_items?.reduce((total, tradeItem) => {
      return total + tradeItem.budget;
    }, 0) ?? 0;
  }

  get isTransferred() {
    return !falsey(this.transferred_at);
  }

  get isBackCharge() {
    return !falsey(this.package_id) &&
      this.type === VariationOrderType.other &&
      !falsey(this.transferred_from_variation_order_id);
  }

  get hasBackCharge() {
    return !falsey(this.transferred_to_variation_order_id) &&
      !falsey(this.transferred_to_variation_order?.package_id) &&
      this.transferred_to_variation_order?.type === VariationOrderType.other;
  }

  get drawnDown() {
    return this.costs?.reduce((total, cost) => total + (cost.current_certified ?? 0), 0) ?? 0;
  }

  get statusTitle() {
    return i18n.t(`Models.VariationOrder.statusTitles.${enumKeyFromValue(VariationOrderStatus, this.status)}`);
  }

  get statusColour() {
    return VariationOrderStatusColours[enumKeyFromValue(VariationOrderStatus, this.status)];
  }

  get statusTextColour() {
    return VariationOrderStatusTextColours[enumKeyFromValue(VariationOrderStatus, this.status)];
  }

  public previous(claim: Claim) {
    if(!falsey(this.$previous)) {
      return this.$previous;
    }

    const previousCosts = useRepo(VariationOrderCost)
      .where('variation_order_id', this.id)
      .whereHas('claim', (query) => {
        query.where('id', (id) => {
          return id !== claim.id;
        })
          .where('end', (end) => {
            return end < claim.end;
          });
      }).get();

    let totalPrevious = 0;

    previousCosts.forEach((cost) => {
      totalPrevious += cost.current_certified;
    });

    return totalPrevious;
  }

  get totalClaim() {
    return this.$totalClaim;
  }

  get totalCertified() {
    return this.$totalCertified;
  }

  get hasMedia() {
    return this.$hasMedia;
  }

  get createdAt() {
    return this.created_at ? DateTime.fromISO(this.created_at) : undefined;
  }
  get submittedAt() {
    return this.submitted_at ? DateTime.fromISO(this.submitted_at) : undefined;
  }
  get provisionedAt() {
    return this.provisioned_at ? DateTime.fromISO(this.provisioned_at) : undefined;
  }
  get approvedAt() {
    return this.approved_at ? DateTime.fromISO(this.approved_at) : undefined;
  }
  get registeredAt() {
    return this.registered_at ? DateTime.fromISO(this.registered_at) : undefined;
  }
  get noticeSentAt() {
    return this.notice_sent_at ? DateTime.fromISO(this.notice_sent_at) : undefined;
  }
  get dateOfOccurrence() {
    return this.date_of_occurrence ? DateTime.fromISO(this.date_of_occurrence) : undefined;
  }

  get nextActionDueIn() {
    const projectsStore = useProjectsStore();
    const project = projectsStore.models.find(this.project_id);
    let dueDate = undefined;

    if(!this.date_of_occurrence || !this.requiresNotice) {
      return undefined;
    }

    if(!this.notice_sent_at && project.head_contract_settings.nov_required) {
      dueDate = project.head_contract_settings.nov_business_days ?
        plusBusinessDays(
          this.dateOfOccurrence,
          project.head_contract_settings.holidays,
          project.head_contract_settings.nov_period,
        ) :
        this.dateOfOccurrence.plus({ days: project.head_contract_settings.nov_period });
    } else if(
      this.status < VariationOrderStatus.submitted &&
      project.head_contract_settings.variation_submission_required
    ) {
      dueDate = project.head_contract_settings.variation_submission_business_days ?
        plusBusinessDays(
          this.dateOfOccurrence,
          project.head_contract_settings.holidays,
          project.head_contract_settings.variation_submission_period,
        ) :
        this.dateOfOccurrence.plus({
          days: project.head_contract_settings.variation_submission_period,
        });
    }

    return dueDate ? Math.ceil(dueDate.diffNow('days').days) : undefined;
  }

  get requiresNotice() {
    return this.type !== VariationOrderType.provisional_sum;
  }

  get CodeRef() {
    return this.client_ref ?? this.code;
  }

  public getActualForecastValue() {
    const amounts = this.actual_forecast_amount || [];

    return amounts.length > 0 ? amounts[amounts.length - 1] : 0;
  }

  public actualForecastForClaim(claim: Claim) {
    const claimIndex = (claim.claim_number ?? 1) - 1;
    const forecastAmounts = this.actual_forecast_amount || [];

    return forecastAmounts[claimIndex] ?? 0;
  }

  public previousActualForecastForClaim(claim: Claim) {
    const claimIndex = (claim.claim_number ?? 1) - 1;
    const forecastAmounts = this.actual_forecast_amount || [];

    return forecastAmounts[claimIndex - 1] ?? 0;
  }

  public subcontractSummaryClaim(variationOrderCostIds: Id[]) {
    return useRepo(VariationOrderCost)
      .where('$head_contract_variation_order_id', this.id)
      .whereIn('id', variationOrderCostIds)
      .get()
      .reduce((total, current) => {
        return total += current.$summaryClaim ?? 0;
      }, 0);
  }

  public subcontractSummaryCertified(variationOrderCostIds: Id[]) {
    return useRepo(VariationOrderCost)
      .where('$head_contract_variation_order_id', this.id)
      .whereIn('id', variationOrderCostIds)
      .get()
      .reduce((total, current) => {
        return total += current.$summaryCertified ?? 0;
      }, 0);
  }
  /* --- end user code --- */
}
